
import { computed, defineComponent } from 'vue';
import { datetimeFormat } from '@/utils';
import { useCoupon } from '@/compositions/useCoupon';
import { usePoint } from '@/compositions/usePoint';
import { Coupon } from '@/repositories/couponRepository';
import dayjs from 'dayjs';
import { i18n } from '@/plugins/i18n';
import CouponPrice from '@/components/molecules/coupon/CouponPrice.vue';

type Props = {
  coupon: Coupon;
};

export default defineComponent<Props>({
  name: 'CouponDetail',

  props: {
    coupon: {
      type: Object,
      required: true,
    },
    restriction: {
      type: String,
      required: false,
    },
  },

  components: {
    CouponPrice,
  },

  setup(props: Props) {
    const { outOfStock } = useCoupon();
    const { enabled: pointEnabled } = usePoint();

    const name = computed(() => props.coupon.couponMaster.name);

    const overview = computed(() => {
      try {
        return JSON.parse(props.coupon.couponMaster.attrs).overview || null;
      } catch {
        return null;
      }
    });

    const mainImageUrl = computed(() => {
      try {
        return JSON.parse(props.coupon.couponMaster.attrs).mainImageUrl || null;
      } catch {
        return null;
      }
    });

    /**
     * CS-9138 画像アスペクト比を変更可能に
     */
    const imageAspect = computed(() => {
      try {
        const str = JSON.parse(props.coupon.couponMaster.attrs);
        const aspect = str.imageAspect || '16 / 9';
        return aspect.replace(' / ', 'by');
      } catch {
        return '16by9';
      }
    });

    const discount = computed(() => JSON.parse(props.coupon.couponMaster.attrs).discount);

    const hasDiscount = computed(() => {
      if (discount.value.type === 'FREE') return false;
      if (discount.value.type === 'CONST' && discount.value.value === 0) return false;
      if (discount.value.type === 'RATE' && discount.value.value === 0) return false;
      return true;
    });

    const point = computed(() => props.coupon.couponMaster.point);

    /**
     * CS-6783 取得前クーポンの期限表示を管理画面へ寄せる
     */
    const expiresAt = computed(() => {
      const expires = JSON.parse(props.coupon.couponMaster.attrs).expires;
      // @note 0が来うるので != null判定
      if (expires.term != null) {
        return (
          `${i18n.t('keyword.coupon')}交換後 ${expires.term}日間` +
          (expires.termStartTime ? `<br/>（毎日${expires.termStartTime}から起算）` : '')
        );
      }
      if (expires.startAt && expires.endAt)
        return `${datetimeFormat(expires.startAt, 'date')} 〜 ${datetimeFormat(expires.endAt, 'date')}`;
      if (expires.startAt) return `${datetimeFormat(expires.startAt, 'date')} 〜`;
      if (expires.endAt) return `〜 ${datetimeFormat(expires.endAt, 'date')}`;
      return 'なし'; // 有効期限なし
    });

    const disabled = computed(() => outOfStock(props.coupon));

    return {
      pointEnabled,
      name,
      overview,
      mainImageUrl,
      discount,
      hasDiscount,
      point,
      expiresAt,
      disabled,
      imageAspect,
    };
  },
});
